import React, { Component } from "react";
import PropTypes from "prop-types";
import FileSaver from "file-saver";
import { Button } from "react-bootstrap";
import utils from "@utils";
import { ReportService } from "@services";
import csvDownload from "json-to-csv-export";
import AnalyticsService from "@services/AnalyticsService";
import { ANALYTICS_EVENTS_ACTIONS, ANALYTICS_EVENTS_CATEGORIES } from "@constants/analyticsEvents";
import ReportBuilder from "../utils/reportBuilder";
import { Loader } from "../../../../components";
import ReportEmbedder from "../utils/reportEmbedder";

const manualInvoiceReportTypes = ["manual_invoice", "manual_aging", "applied_payments", "deposit"];
class Report extends Component {
	constructor(props) {
		super(props);
		const { params } = props;

		this.state = {
			params,
			isLoading: false,
			validationMessage: "",
		};
	}

	componentDidUpdate({ params: prevParams }) {
		const { params } = this.props;
		if (prevParams !== params) {
			// This ESLint rule could possibly be disabled permanently. Further discussions will be necessary, but disabling in this file for right now
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ params });
		}
	}

	setLoading = (value, callback) => {
		this.setState({ isLoading: value }, callback);
	};

	setValidationMessage = value => {
		this.setState({ validationMessage: value });
	};

	exportReport = format => event => {
		event.preventDefault();
		const { lang } = global;
		const {
			type: reportType,
			user: { TimeZoneId },
			user,
			type,
		} = this.props;
		const { params: model } = this.state;
		this.setState({ isLoading: true });
		const report = ReportService.getReportSettingByType(reportType);
		AnalyticsService.trackEvent({
			category: ANALYTICS_EVENTS_CATEGORIES.REPORT_EXPORT,
			action: ANALYTICS_EVENTS_ACTIONS.REPORT_EXPORTED,
			customParameters: {
				reportType,
				reportName: report.name,
				format,
			},
		});
		if (
			(manualInvoiceReportTypes.includes(reportType) ||
				reportType === "invoice_aging" ||
				reportType === "agency_overview" ||
				reportType === "calloff") &&
			format === "xls"
		) {
			const { data: rawData } = model;
			let fileName = "NewRKReport";
			let data = rawData;
			let totals = {};
			const depositIds = [];
			switch (reportType) {
				case "project_overview":
					fileName = reportType;
					data = ReportBuilder[`render_${type}`](model, user, false, true);
					break;
				case "agency_overview":
					fileName = reportType;
					data = ReportBuilder[`render_${type}`](model, user, false, false, true);
					break;
				case "fairness":
					fileName = reportType;
					data = ReportBuilder[`render_${type}`](model, true);
					break;
				case "manual_invoice":
					fileName = "ManualInvoiceReport";
					data = rawData.map(item => ({
						"Invoice Number": item.InvoiceNumber,
						From: utils.formatDate(
							`${item.EarliestJobStartTime}Z`,
							"en-US",
							undefined,
							"numeric",
						),
						To: utils.formatDate(
							`${item.LatestJobStartTime}Z`,
							"en-US",
							undefined,
							"numeric",
						),
						"Client Name": item.ClientName || "Unknown",
						"Officer Pay": utils.currencyFormatter(item.OfficerPay),
						"Agency Fees": utils.currencyFormatter(item.AgencyFees),
						"Vehicle Fees": utils.currencyFormatter(item.VehicleFees),
						"Equipment Fees": utils.currencyFormatter(item.EquipmentFees),
						"Miscellaneous Fees": utils.currencyFormatter(item.OtherFees),
						Total: utils.currencyFormatter(item.Total),
						"Paid Amount": utils.currencyFormatter(item.PaidAmount),
						Balance: utils.currencyFormatter(item.Balance),
					}));
					break;
				case "manual_aging":
				case "invoice_aging":
					fileName =
						reportType === "invoice_aging"
							? "InvoiceAgingReport"
							: "ManualInvoiceAgingReport";
					totals = {
						"Client ID": `Totals (as of ${utils.formatDate(new Date(), "en-US", undefined)}):`,
						"Client Name": "",
						Total: 0,
						Current: 0,
						"1-30 Days": 0,
						"31-60 Days": 0,
						"60+ Days": 0,
					};
					data = rawData.map(item => {
						totals.Total += item.Total;
						totals.Current += item.Current;
						totals["1-30 Days"] += item.OneToThirtyDays;
						totals["31-60 Days"] += item.ThirtyOneToSixtyDays;
						totals["60+ Days"] += item.GreaterThanSixtyDays;

						return {
							"Client ID": item.ClientId,
							"Client Name": item.ClientName || "Unknown",
							Total: utils.currencyFormatter(item.Total),
							Current: utils.currencyFormatter(item.Current),
							"1-30 Days": utils.currencyFormatter(item.OneToThirtyDays),
							"31-60 Days": utils.currencyFormatter(item.ThirtyOneToSixtyDays),
							"60+ Days": utils.currencyFormatter(item.GreaterThanSixtyDays),
						};
					});

					totals = Object.fromEntries(
						Object.entries(totals).map(([key, value]) => {
							if (key !== "Client ID" && key !== "Client Name")
								return [key, utils.currencyFormatter(value)];
							return [key, value];
						}),
					);
					data.push({});
					data.push(totals);
					break;
				case "applied_payments":
					fileName = "AppliedPaymentsReport";
					data = rawData.map(item => ({
						"Client Name": item.ClientName || "Unknown",
						"Invoice Number": item.InvoiceNumber,
						Amount: utils.currencyFormatter(item.PaymentAmount),
						"Paid By": item.PaymentUserFullName,
						"Paid On": utils.formatDate(
							new Date(`${item.PaymentCreatedDateTimeUTC}Z`),
							"en-US",
							TimeZoneId,
							"dt-num",
						),
						Comments: item.PaymentComments,
					}));
					break;
				case "deposit":
					fileName = "DepositReport";
					data = rawData.map(item => {
						let hideInfo = false;
						let invoiceIdContent = item.InvoiceId;

						if (!item.IsRKPay) {
							invoiceIdContent = item.InvoiceNumber;
						}

						if (item.DepositId !== null && item.DepositId !== undefined) {
							if (!depositIds.includes(item.DepositId)) {
								depositIds.push(item.DepositId);
							} else {
								hideInfo = true;
							}
						}

						return {
							"Invoice Number": !hideInfo ? invoiceIdContent : "",
							"Deposited on": !hideInfo
								? utils.formatDate(
										new Date(`${item.DepositedOnDate}Z`),
										"en-US",
										TimeZoneId,
										"dt-num",
									)
								: "",
							"Deposit Total":
								!hideInfo && item.Total ? utils.currencyFormatter(item.Total) : "",
							"Client Name": !hideInfo ? item.ClientName || "Unknown" : "",
							"Officer Name": item.OfficerName || "Unknown",
							"Officer Pay": item.OfficerPay
								? utils.currencyFormatter(item.OfficerPay)
								: "",
							"Job Rate": item.Rate,
							"Invoiced Hours": item.Hours,
							"LEA Pay": item.LEAPay ? utils.currencyFormatter(item.LEAPay) : "",
							"Agency Fees": item.AgencyFees
								? utils.currencyFormatter(item.AgencyFees)
								: "",
							"Coordinator Fees": item.CoordinatorFees
								? utils.currencyFormatter(item.CoordinatorFees)
								: "",
							"Equipment Fees": item.EquipmentFees
								? utils.currencyFormatter(item.EquipmentFees)
								: "",
							"Vehicle Fees": item.VehicleFees
								? utils.currencyFormatter(item.VehicleFees)
								: "",
							"Additional Fees":
								!hideInfo && item.OtherFees
									? utils.currencyFormatter(item.OtherFees)
									: "",
							"Deposit Id": !hideInfo ? item.DepositId : "",
						};
					});
					break;
				default:
					fileName = reportType;
					data = ReportBuilder[`render_${type}`](model, user, true);
					break;
			}
			fileName += ".csv";
			csvDownload({ data, filename: fileName, delimiter: "," });
			setTimeout(() => {
				this.setState({ isLoading: false });
			}, 500);
		} else {
			const { filters } = this.props;

			ReportService.exportReport({
				reportType,
				model,
				filters,
				type: format,
				locationName: reportType === "location_overview" ? model.LocationName : undefined,
			})
				.then(response => {
					if (response.data.FileContents) {
						const rawData = response.data;
						// Force download the file
						FileSaver.saveAs(
							utils.base64ToBlob(rawData.FileContents, rawData.ContentType),
							rawData.FileDownloadName,
						);

						this.setState({ isLoading: false });
					} else {
						const rawData = response.data;
						const contentDisposition = response.headers["content-disposition"];
						let fileName;

						if (contentDisposition) {
							fileName = utils.getFileNameByContentDisposition(contentDisposition);
						}

						utils.downloadReportFromBlobResponse(rawData, fileName || reportType);
						this.setState({ isLoading: false });
					}

					utils.notify({
						type: "success",
						message: lang.t("Report file successfully generated"),
					});
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error generating report file"),
						title: "Oops",
					});
					this.setState({ isLoading: false });
				});
		}
	};

	render() {
		const { type, renderReport, user, source } = this.props;
		const { params, isLoading, validationMessage, domoLoading } = this.state;

		const title = ReportService.getReportSettingByType(type);

		if (!renderReport && source !== 'domo') {
			return (
				<div className="panel-container">
					<div className="empty-panel">
						<h4>
							<Choose>
								<When condition={type === ""}>Please select a Report Type</When>
								<When condition={validationMessage !== ""}>
									{validationMessage}
								</When>
								<Otherwise>Report Area</Otherwise>
							</Choose>
						</h4>
					</div>
				</div>
			);
		}

        if (!renderReport && source === 'domo') {
            return(
                <div className="domo-loading-container">
                    <Loader active={domoLoading} />
                </div>
            );
        }

		return (
			<div className="panel-container">
				<Choose>
					<When condition={Object.values(params)?.length === 0}>
						<div className="empty-panel" />
					</When>
					<When condition={params?.reportLink?.length > 0}>
						<ReportEmbedder embedKey={params?.reportLink} />
					</When>
					<Otherwise>
						<div className="flex flex-align-items-center margin-bottom-25">
							<div className="flex-auto">
								<div className="font-size-24 color-gray-oxford">{title.name}</div>
							</div>
							<div className="hidden-print">
								<If
									condition={
										!manualInvoiceReportTypes.includes(type) &&
										type !== "invoice_aging"
									}
								>
									<Button
										id="export-to-pdf"
										bsStyle="link"
										className="btn-icon-only"
										onClick={this.exportReport("pdf")}
									>
										<i className="rkf rkf-pdf font-size-20 color-black" />
									</Button>
								</If>
								<Button
									id="export-to-csv"
									bsStyle="link"
									className="btn-icon-only"
									onClick={this.exportReport("xls")}
								>
									<i className="rkf rkf-csv font-size-20 color-black" />
								</Button>
								<Button
									id="print-report"
									bsStyle="link"
									className="btn-icon-only"
									onClick={() => {
										const report = ReportService.getReportSettingByType(type);
										AnalyticsService.trackEvent({
											category: ANALYTICS_EVENTS_CATEGORIES.REPORT_EXPORT,
											action: ANALYTICS_EVENTS_ACTIONS.REPORT_EXPORTED,
											customParameters: {
												reportType: type,
												reportName: report.name,
												format: "print",
											},
										});
										window.print();
									}}
								>
									<i className="rkf rkf-printer font-size-20 color-black" />
								</Button>
							</div>
						</div>
						{ReportBuilder[`render_${type}`](
							{
								...params,
								isLEA: user.isLEA(),
								reportType: type,
								isCoordinator: user.isCoordinator(),
							},
							user,
						)}
					</Otherwise>
				</Choose>
				<Loader active={isLoading} />
			</div>
		);
	}
}

Report.propTypes = {
	type: PropTypes.string.isRequired,
	filters: PropTypes.object.isRequired,
	params: PropTypes.object.isRequired,
	user: PropTypes.object,
	renderReport: PropTypes.bool,
    source: PropTypes.string,
};

Report.defaultProps = {
	user: {},
	renderReport: false,
    source: "",
};

export default Report;
